import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, CircleDot, FileText, History, MessageSquare, PenLine, Settings2, ShieldCheck } from 'lucide-react'
import type { AuditEntry } from '@/api/ticketsApi'
import { EmptyState, ErrorState, LoadingState, errorMessage } from '@/components/common/States'
import { useAgents } from '@/hooks/useDirectory'
import { useTicketAudit, useVerifyAudit } from '@/hooks/useTickets'
import { categoryOf, describe, groupByDay, isSystemEvent, type AuditCategory } from '@/lib/auditView'
import { timeLabel } from '@/lib/threadView'
import { cn } from '@/lib/utils'

const FILTERS = ['all', 'status', 'messages', 'files', 'changes'] as const
type Filter = (typeof FILTERS)[number]

const ICON: Record<AuditCategory, typeof CircleDot> = {
  status: CircleDot,
  messages: MessageSquare,
  files: FileText,
  changes: PenLine,
  system: Settings2,
}

const DOT: Record<AuditCategory, string> = {
  status: 'bg-status-progress-bg text-status-progress-fg',
  messages: 'bg-accent text-primary',
  files: 'bg-surface-subtle text-ink',
  changes: 'bg-note-bg text-note-heading',
  system: 'bg-surface-subtle text-ink-muted',
}

/**
 * The ticket's audit trail as a readable timeline: one sentence per entry,
 * grouped by day, newest first; filters by kind; SLA bookkeeping behind
 * "Show system events". Every entry still opens to its full record (sequence,
 * hashes, before / after, actor id, context), and "Verify chain" recomputes
 * the hash chain against the daily anchor.
 */
export function AuditTab({ routeId, timeZone }: { routeId: string; timeZone: string }) {
  const { t } = useTranslation()
  const audit = useTicketAudit(routeId, true)
  const verify = useVerifyAudit(routeId)
  const agents = useAgents()
  const [filter, setFilter] = useState<Filter>('all')
  const [showSystem, setShowSystem] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())

  const entries = useMemo(() => audit.data ?? [], [audit.data])
  const systemCount = entries.filter(isSystemEvent).length
  const shown = entries.filter((e) => (showSystem || !isSystemEvent(e)) && (filter === 'all' || categoryOf(e) === filter))
  const days = groupByDay(shown, timeZone)
  const nameOf = (id: string) => agents.data?.find((a) => a.id === id)?.name
  const sentence = (e: AuditEntry) => {
    const s = describe(e, nameOf)
    const params = { ...s.params, ...(s.params.field ? { field: t(`audit.field.${s.params.field}`, { defaultValue: s.params.field }) } : {}) }
    return t(s.key, { ...params, defaultValue: e.eventType.replace(/_/g, ' ') })
  }
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-meta text-ink-muted">{t('audit.intro')}</p>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => verify.mutate()}
            disabled={verify.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-line-strong px-3 py-1.5 text-meta font-medium text-ink hover:bg-surface-subtle disabled:opacity-60"
          >
            <ShieldCheck className="size-4" aria-hidden />
            {verify.isPending ? t('audit.verifying') : t('audit.verify')}
          </button>
          {verify.data ? (
            <p role="status" className={cn('text-label', verify.data.valid ? 'text-resolved-heading' : 'font-medium text-status-escalated-fg')}>
              {verify.data.valid
                ? t('audit.valid', { length: verify.data.length, hash: (verify.data.headHash ?? '—').slice(0, 16) })
                : t('audit.invalid', { seq: verify.data.brokenAtSeq ?? '?', reason: verify.data.reason ?? '' })}
              {verify.data.valid && verify.data.anchor?.matches ? ` · ${t('audit.anchored', { day: verify.data.anchor.day })}` : ''}
            </p>
          ) : verify.error ? (
            <p role="alert" className="text-label text-status-escalated-fg">
              {errorMessage(verify.error)}
            </p>
          ) : null}
        </div>
      </div>

      {audit.isPending ? (
        <LoadingState />
      ) : audit.isError ? (
        <ErrorState message={t('audit.loadError', { message: errorMessage(audit.error) })} onRetry={() => void audit.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState compact icon={History} message={t('audit.empty')} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('audit.columns.event')}>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filter === f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-label font-medium',
                    filter === f ? 'border-primary bg-primary text-white' : 'border-line text-ink-muted hover:bg-surface-subtle hover:text-ink',
                  )}
                >
                  {t(`audit.filters.${f}`)}
                </button>
              ))}
            </div>
            {systemCount > 0 ? (
              <label className="flex items-center gap-2 text-label text-ink-muted">
                <input type="checkbox" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} />
                {t('audit.showSystem', { count: systemCount })}
              </label>
            ) : null}
          </div>

          {days.length === 0 ? (
            <p className="text-meta text-ink-muted">{t('audit.nothingHere')}</p>
          ) : (
            days.map((d) => (
              <section key={d.day} className="flex flex-col">
                <h4 className="mb-2 text-eyebrow font-medium uppercase text-ink-muted">{d.day}</h4>
                <ol className="relative flex flex-col border-l border-line pl-5">
                  {d.entries.map((e) => {
                    const cat = categoryOf(e)
                    const Icon = ICON[cat]
                    const expanded = open.has(e.id)
                    return (
                      <li key={e.id} className="relative pb-3 last:pb-0">
                        <span className={cn('absolute -left-[31px] top-0.5 flex size-5 items-center justify-center rounded-full ring-4 ring-surface', DOT[cat])}>
                          <Icon className="size-3" aria-hidden />
                        </span>
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                          <p className="text-meta text-ink-strong">
                            {sentence(e)}
                            {e.visibility === 'customer' ? (
                              <span className="ml-2 rounded-sm bg-status-resolved-bg px-1.5 py-px text-label text-status-resolved-fg">{t('audit.customerVisible')}</span>
                            ) : null}
                          </p>
                          <span className="font-mono text-label text-ink-muted">{timeLabel(e.at, timeZone)}</span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-label text-ink-muted">
                          <span>{e.actorName ?? e.actorKind}</span>
                          {e.reason ? <span className="italic">“{e.reason}”</span> : null}
                          <button type="button" onClick={() => toggle(e.id)} className="inline-flex items-center gap-0.5 font-medium text-ink hover:underline" aria-expanded={expanded}>
                            {expanded ? <ChevronDown className="size-3" aria-hidden /> : <ChevronRight className="size-3" aria-hidden />}
                            {expanded ? t('audit.hideDetails') : t('audit.details')}
                          </button>
                        </div>
                        {expanded ? <EntryDetails e={e} /> : null}
                      </li>
                    )
                  })}
                </ol>
              </section>
            ))
          )}
        </>
      )}
    </div>
  )
}

/** The full record of one entry, for GxP review: nothing summarised away. */
function EntryDetails({ e }: { e: AuditEntry }) {
  const { t } = useTranslation()
  const json = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v, null, 2))
  const rows: [string, string][] = [
    [t('audit.detail.seq'), `#${e.seq} · ${e.eventType}`],
    ...(e.field ? ([[t('audit.detail.field'), e.field]] as [string, string][]) : []),
    [t('audit.detail.old'), json(e.oldValue)],
    [t('audit.detail.new'), json(e.newValue)],
    ...(e.reason ? ([[t('audit.detail.reason'), e.reason]] as [string, string][]) : []),
    ...(e.metadata ? ([[t('audit.detail.metadata'), json(e.metadata)]] as [string, string][]) : []),
    [t('audit.detail.actorId'), `${e.actorKind} · ${e.actorId ?? '—'}`],
    [t('audit.detail.hash'), e.hash],
    [t('audit.detail.prevHash'), e.prevHash ?? '—'],
  ]
  return (
    <dl className="mt-2 grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 rounded-md border border-line bg-surface-subtle px-3 py-2 text-label">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-ink-muted">{k}</dt>
          <dd className="whitespace-pre-wrap break-all font-mono text-ink">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
